import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Button, Collapse, Dialog, DialogActions, DialogContent, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { usdaNameToCommonName, usdaNameToUnit } from "@notemeal/nutrient-utils";
import { ALL_FORM_NUTRIENTS, NutrientAmountState } from "shared-ui/NutrientAmount/customNutrientUtils";
import { round } from "shared-ui/utils/numbers";
import { inputToNumber } from "shared-ui/utils/inputUtils";
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import DialogTitle from "../../../componentLibrary/DialogTitle";
import { BaseFoodFormType } from "../FoodFormSchema/FoodFormSchema";
import { FoodServingType } from "../FoodFormSchema/FoodServingSchema";
import { MACRO_KEY_TO_PER_100G_FIELD, changeNutrientAmounts } from "../FoodFormSchema/foodServingsUtils";

type NutrientKey = (typeof ALL_FORM_NUTRIENTS)[number];

const CATEGORY_COLORS: Record<string, string> = {
  Carbohydrates: "#A6EF67",
  Proteins: "#A4BCFD",
  Lipids: "#FCA5A5",
  Vitamins: "#FEE172",
  Minerals: "#C3B4FD",
  Other: "#D0D5DD",
};

// --- Hierarchy definitions ---

interface NutrientNode {
  nutrient: NutrientKey;
  label?: string;
  children?: NutrientNode[];
  computed?: boolean;
}

const CARBS_HIERARCHY: NutrientNode[] = [
  {
    nutrient: "Carbohydrate, by difference",
    children: [
      { nutrient: "Fiber, total dietary" },
      { nutrient: "Fiber, total dietary", label: "Net (non-fiber)", computed: true },
      { nutrient: "Starch" },
      {
        nutrient: "Sugars, total including NLEA",
        label: "Total Sugars",
        computed: true,
        children: [
          { nutrient: "Sugars, total including NLEA" },
          { nutrient: "Sugars, added" },
        ],
      },
    ],
  },
];

const PROTEINS_HIERARCHY: NutrientNode[] = [
  {
    nutrient: "Protein",
    children: [
      {
        nutrient: "Protein",
        label: "Non-essential",
        computed: true,
        children: [
          { nutrient: "Alanine" },
          { nutrient: "Arginine" },
          { nutrient: "Cystine" },
          { nutrient: "Glutamic acid" },
          { nutrient: "Glutamine" },
          { nutrient: "Glycine" },
          { nutrient: "Proline" },
          { nutrient: "Serine" },
          { nutrient: "Tyrosine" },
        ],
      },
      {
        nutrient: "Protein",
        label: "Essential",
        computed: true,
        children: [
          { nutrient: "Histidine" },
          { nutrient: "Isoleucine" },
          { nutrient: "Leucine" },
          { nutrient: "Lysine" },
          { nutrient: "Methionine" },
          { nutrient: "Phenylalanine" },
          { nutrient: "Threonine" },
          { nutrient: "Tryptophan" },
          { nutrient: "Valine" },
        ],
      },
    ],
  },
];

const LIPIDS_HIERARCHY: NutrientNode[] = [
  {
    nutrient: "Total lipid (fat)",
    children: [
      { nutrient: "Fatty acids, total monounsaturated" },
      {
        nutrient: "Fatty acids, total polyunsaturated",
        children: [
          { nutrient: "18:3 n-3 c,c,c (ALA)" },
          { nutrient: "20:5 n-3 (EPA)" },
          { nutrient: "22:5 n-3 (DPA)" },
          { nutrient: "22:6 n-3 (DHA)" },
          { nutrient: "18:2 n-6 c,c" },
        ],
      },
      { nutrient: "Fatty acids, total saturated" },
      { nutrient: "Fatty acids, total trans" },
    ],
  },
  { nutrient: "Cholesterol" },
];

const VITAMINS_FLAT: NutrientKey[] = [
  "Vitamin A, RAE",
  "Vitamin E (alpha-tocopherol)",
  "Vitamin D (D2 + D3), International Units",
  "Vitamin C, total ascorbic acid",
  "Thiamin",
  "Riboflavin",
  "Niacin",
  "Pantothenic acid",
  "Vitamin B-6",
  "Biotin",
  "Vitamin B-12",
  "Folate, DFE",
  "Vitamin K (phylloquinone)",
];

const MINERALS_FLAT: NutrientKey[] = [
  "Calcium, Ca",
  "Iron, Fe",
  "Magnesium, Mg",
  "Phosphorus, P",
  "Potassium, K",
  "Sodium, Na",
  "Zinc, Zn",
  "Copper, Cu",
  "Manganese, Mn",
  "Selenium, Se",
];

const OTHER_FLAT: NutrientKey[] = ["Alcohol, ethyl", "Caffeine", "Choline, total", "Water"];

// --- Components ---

interface ViewAllNutrientsDialogProps {
  open: boolean;
  onClose: () => void;
  form: UseFormReturn<BaseFoodFormType>;
  disabled?: boolean;
}

export const ViewAllNutrientsDialog = ({ open, onClose, form, disabled }: ViewAllNutrientsDialogProps) => {
  const { watch } = form;
  const [watchedUsdaFdcId, watchedServings] = watch(["usdaFdcId", "servings"]);
  const defaultServing = watchedServings?.find((s: FoodServingType) => s.isDefault);
  const isDisabled = disabled || watchedUsdaFdcId !== null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle title="Nutrient Analysis" onClose={onClose} />
      <DialogContent>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, alignItems: "start" }}>
          <Card label="Carbohydrates">
            {defaultServing && (
              <HierarchySection
                nodes={CARBS_HIERARCHY}
                serving={defaultServing}
                form={form}
                disabled={isDisabled}
              />
            )}
          </Card>
          <Card label="Proteins">
            {defaultServing && (
              <HierarchySection
                nodes={PROTEINS_HIERARCHY}
                serving={defaultServing}
                form={form}
                disabled={isDisabled}
              />
            )}
          </Card>
          <Card label="Lipids">
            {defaultServing && (
              <HierarchySection
                nodes={LIPIDS_HIERARCHY}
                serving={defaultServing}
                form={form}
                disabled={isDisabled}
              />
            )}
          </Card>
          <Card label="Vitamins">
            {defaultServing && (
              <FlatSection nutrients={VITAMINS_FLAT} serving={defaultServing} form={form} disabled={isDisabled} />
            )}
          </Card>
          <Card label="Minerals">
            {defaultServing && (
              <FlatSection nutrients={MINERALS_FLAT} serving={defaultServing} form={form} disabled={isDisabled} />
            )}
          </Card>
          <Card label="Other">
            {defaultServing && (
              <FlatSection nutrients={OTHER_FLAT} serving={defaultServing} form={form} disabled={isDisabled} />
            )}
          </Card>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Card shell ---

const Card = ({ label, children }: { label: string; children: React.ReactNode }) => {
  const color = CATEGORY_COLORS[label] || "#D0D5DD";
  return (
    <Box sx={{ border: "1px solid #D0D5DD", borderRadius: "8px", overflow: "hidden", background: "#fff" }}>
      <Box sx={{ backgroundColor: color, height: 12 }} />
      <Box sx={{ p: "16px", display: "flex", flexDirection: "column", gap: "10px", overflow: "auto" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        {children}
      </Box>
    </Box>
  );
};

// --- Flat section (Vitamins, Minerals, Other) ---

interface FlatSectionProps {
  nutrients: NutrientKey[];
  serving: FoodServingType;
  form: UseFormReturn<BaseFoodFormType>;
  disabled?: boolean;
}

const FlatSection = ({ nutrients, serving, form, disabled }: FlatSectionProps) => (
  <>
    {nutrients.map(n => (
      <NutrientInputRow key={n} nutrient={n} serving={serving} form={form} disabled={disabled} />
    ))}
  </>
);

// --- Hierarchy section (Carbs, Proteins, Lipids) ---

interface HierarchySectionProps {
  nodes: NutrientNode[];
  serving: FoodServingType;
  form: UseFormReturn<BaseFoodFormType>;
  disabled?: boolean;
}

const HierarchySection = ({ nodes, serving, form, disabled }: HierarchySectionProps) => (
  <>
    {nodes.map((node, i) => (
      <ParentNode key={i} node={node} serving={serving} form={form} disabled={disabled} />
    ))}
  </>
);

interface ParentNodeProps {
  node: NutrientNode;
  serving: FoodServingType;
  form: UseFormReturn<BaseFoodFormType>;
  disabled?: boolean;
}

const ParentNode = ({ node, serving, form, disabled, depth = 0 }: ParentNodeProps & { depth?: number }) => {
  const [expanded, setExpanded] = useState(!node.computed);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px", pl: `${depth * 20}px` }}>
        {hasChildren && (
          <Box
            component="button"
            type="button"
            aria-expanded={expanded}
            aria-label={`${expanded ? "Collapse" : "Expand"} ${node.label ?? node.nutrient}`}
            onClick={() => setExpanded(!expanded)}
            sx={{ background: "none", border: "none", p: 0, m: 0, cursor: "pointer", display: "flex", alignItems: "center", width: 16, height: 16, flexShrink: 0 }}
          >
            {expanded ? <KeyboardArrowUpIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
          </Box>
        )}
        {node.computed ? (
          <ComputedRow label={node.label!} serving={serving} />
        ) : (
          <NutrientInputRow nutrient={node.nutrient} serving={serving} form={form} disabled={disabled} />
        )}
      </Box>
      {hasChildren && expanded &&
        node.children!.map((child, i) =>
          child.children ? (
            <ParentNode key={i} node={child} serving={serving} form={form} disabled={disabled} depth={depth + 1} />
          ) : child.computed ? (
            <Box key={i} sx={{ pl: `${(depth + 1) * 20}px` }}>
              <ComputedRow label={child.label!} serving={serving} />
            </Box>
          ) : (
            <Box key={child.nutrient + i} sx={{ pl: `${(depth + 1) * 20}px` }}>
              <NutrientInputRow nutrient={child.nutrient} serving={serving} form={form} disabled={disabled} />
            </Box>
          )
        )}
    </>
  );
};

// --- Shared input sx ---

const inputFieldSx = {
  "&.MuiTextField-root": {
    marginTop: 0,
  },
  width: 100,
  flexShrink: 0,
  "& .MuiOutlinedInput-root": {
    height: 28,
    borderRadius: "6px",
    paddingLeft: 0,
    paddingRight: 0,
    "& input": {
      padding: "0 6px",
      fontSize: 12,
      lineHeight: "16px",
    },
  },
};

const unitAdornmentSx = {
  backgroundColor: "#F2F4F7",
  borderLeft: "1px solid #D0D5DD",
  height: 28,
  maxHeight: "none",
  borderRadius: "0 6px 6px 0",
  px: 0.5,
  ml: 0,
  "& .MuiTypography-root": {
    fontSize: 11,
    color: "text.secondary",
    lineHeight: 1,
    whiteSpace: "nowrap",
  },
};

// --- Computed (read-only) row for Net carbs, Non-essential, Essential ---

interface ComputedRowProps {
  label: string;
  serving: FoodServingType;
}

const NON_ESSENTIAL_AMINO_ACIDS: NutrientKey[] = [
  "Alanine", "Arginine", "Cystine", "Glutamic acid", "Glutamine", "Glycine", "Proline", "Serine", "Tyrosine",
];

const ESSENTIAL_AMINO_ACIDS: NutrientKey[] = [
  "Histidine", "Isoleucine", "Leucine", "Lysine", "Methionine", "Phenylalanine", "Threonine", "Tryptophan", "Valine",
];

const sumNutrients = (nutrients: NutrientKey[], amounts: NutrientAmountState | null): number => {
  if (!amounts) return 0;
  return nutrients.reduce((sum, n) => sum + (inputToNumber(amounts[n] || "0") || 0), 0);
};

const ComputedRow = ({ label, serving }: ComputedRowProps) => {
  const { nutrientAmounts } = serving;

  let value = 0;
  let unit = "g";

  if (label === "Net (non-fiber)") {
    const totalCarbs = inputToNumber(nutrientAmounts?.["Carbohydrate, by difference"] || "0") || 0;
    const fiber = inputToNumber(nutrientAmounts?.["Fiber, total dietary"] || "0") || 0;
    value = Math.max(totalCarbs - fiber, 0);
  } else if (label === "Total Sugars") {
    value = sumNutrients(["Sugars, total including NLEA"], nutrientAmounts);
  } else if (label === "Non-essential") {
    value = sumNutrients(NON_ESSENTIAL_AMINO_ACIDS, nutrientAmounts);
  } else if (label === "Essential") {
    value = sumNutrients(ESSENTIAL_AMINO_ACIDS, nutrientAmounts);
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flex: 1, justifyContent: "space-between" }}>
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{label}</Typography>
      <TextField
        variant="outlined"
        size="small"
        value={round(value)}
        disabled
        inputProps={{ "aria-label": label }}
        sx={inputFieldSx}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={unitAdornmentSx}>
              {unit}
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};

// --- Nutrient input row ---

interface NutrientInputRowProps {
  nutrient: NutrientKey;
  serving: FoodServingType;
  form: UseFormReturn<BaseFoodFormType>;
  disabled?: boolean;
}

const NutrientInputRow = ({ nutrient, serving, form, disabled }: NutrientInputRowProps) => {
  const { setValue, getValues } = form;
  const { nutrientAmounts } = serving;
  const commonName = usdaNameToCommonName(nutrient) || nutrient;
  const unit = usdaNameToUnit(nutrient) || "";

  const displayValue = nutrientAmounts && nutrientAmounts[nutrient]
    ? round(inputToNumber(nutrientAmounts[nutrient] || "0") || 0).toString()
    : "";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flex: 1, justifyContent: "space-between" }}>
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{commonName}</Typography>
      <TextField
        variant="outlined"
        size="small"
        value={displayValue}
        type="number"
        placeholder="0"
        disabled={disabled}
        inputProps={{ min: 0, "aria-label": commonName }}
        onChange={e => {
          const { newNutrientAmountsManualEntry, newServings, macroUpdate } = changeNutrientAmounts({
            foodForm: getValues(),
            nutrientName: nutrient,
            nutrientValueInput: e.target.value,
          });
          setValue("nutrientAmountsManualEntry", newNutrientAmountsManualEntry);
          if (macroUpdate) {
            setValue(MACRO_KEY_TO_PER_100G_FIELD[macroUpdate.macro], macroUpdate.macroValuePer100g);
          }
          newServings.forEach((s, index) => {
            setValue(`servings.${index}.nutrientAmounts`, s.nutrientAmounts);
            if (macroUpdate) {
              setValue(`servings.${index}.${macroUpdate.macro}`, s[macroUpdate.macro], { shouldValidate: true });
            }
          });
        }}
        sx={inputFieldSx}
        InputProps={{
          endAdornment: unit ? (
            <InputAdornment position="end" sx={unitAdornmentSx}>
              {unit}
            </InputAdornment>
          ) : undefined,
        }}
      />
    </Box>
  );
};
